import { learningStepsSchema } from '@mio/shared';
import { ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';

import type { LearningTrace } from '@mio/shared';
import type { KeyboardEvent } from 'react';

import './visual-learning.css';

function WorkedTrace({ trace }: { trace: LearningTrace }) {
  const [index, setIndex] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();
  const step = trace.steps[index];
  if (!step) return null;

  function navigate(event: KeyboardEvent<HTMLButtonElement>, current: number) {
    let target: number;
    if (event.key === 'ArrowRight') target = (current + 1) % trace.steps.length;
    else if (event.key === 'ArrowLeft')
      target = (current - 1 + trace.steps.length) % trace.steps.length;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = trace.steps.length - 1;
    else return;
    event.preventDefault();
    setIndex(target);
    tabs.current[target]?.focus();
  }

  return (
    <section className="learning-trace" aria-labelledby={`${id}-title`} data-trace-index={index}>
      <div className="learning-trace-heading">
        <span>Worked trace</span>
        <span>
          {index + 1} / {trace.steps.length}
        </span>
      </div>
      <h3 id={`${id}-title`}>{trace.title}</h3>
      <div role="tablist" aria-label="Trace steps" className="learning-trace-path">
        {trace.steps.map((item, position) => (
          <button
            key={position}
            type="button"
            ref={(node) => {
              tabs.current[position] = node;
            }}
            role="tab"
            data-trace-step={position}
            id={`${id}-step-${position}`}
            aria-label={`Step ${position + 1}: ${item.title}`}
            aria-selected={position === index}
            aria-controls={position === index ? `${id}-state` : undefined}
            tabIndex={position === index ? 0 : -1}
            title={item.title}
            onClick={() => setIndex(position)}
            onKeyDown={(event) => navigate(event, position)}
          >
            <span>{position + 1}</span>
            <span>{item.title}</span>
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${id}-state`}
        aria-labelledby={`${id}-step-${index}`}
        aria-live="polite"
        tabIndex={0}
        className="learning-trace-state"
      >
        <h4>{step.title}</h4>
        <p>{step.detail}</p>
        <dl>
          {step.state.map((entry, position) => (
            <div key={position}>
              <dt>{entry.label}</dt>
              <dd>{entry.value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="learning-trace-actions">
        <button
          type="button"
          onClick={() => setIndex(0)}
          disabled={index === 0}
          aria-label="Restart trace"
          title="Restart trace"
        >
          <RotateCcw size={14} aria-hidden="true" />
        </button>
        <div>
          <button
            type="button"
            disabled={index === 0}
            onClick={() => setIndex((value) => Math.max(0, value - 1))}
          >
            <ArrowLeft size={14} aria-hidden="true" /> Previous
          </button>
          <button
            type="button"
            disabled={index === trace.steps.length - 1}
            onClick={() => setIndex((value) => Math.min(trace.steps.length - 1, value + 1))}
          >
            Next <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

/** The observations are authored alongside the runnable example, with a shared validated shape. */
export function LearningSteps({ source }: { source: string }) {
  const trace = useMemo(() => {
    try {
      const result = learningStepsSchema.safeParse(JSON.parse(source) as unknown);
      return result.success ? result.data : null;
    } catch {
      return null;
    }
  }, [source]);
  if (!trace) {
    return (
      <p role="alert" className="text-fg-muted text-sm">
        This worked trace could not load. Use the runnable example below to follow the mechanism.
      </p>
    );
  }
  return <WorkedTrace key={source} trace={trace} />;
}
